// Search index for the Help & Tutorial Center.
//
// Each entry represents a searchable section across the 11 chapters.
// When real MDX content lands in prompts T-3 / T-4 this file will be
// replaced by a build-time generator (see masterplan §7.1). For now we
// seed ~30 sections covering every chapter so Fuse.js can already match
// the required canonical queries:
//   "score fiabilité" → Chapter 2 · Section 2.3
//   "jours de vie"    → Chapter 3 · Section 3.4
//   "jugement"        → Chapter 7
//   "télécharger"     → Chapter 8
//   "PDF scanné"      → Chapter 10
//
// Anchor format: `section-<chapterId>-<index>` — matches the shell
// placeholder anchors so scroll-to-section keeps working once real MDX
// sections expose matching `id` attributes.

export const SEARCH_INDEX = [
  // ── Chapter 0 — Bienvenue & Philosophie ─────────────────────────────
  {
    id: "0-1",
    chapter: 0,
    title: "La théorie vitaliste en une phrase",
    content:
      "L'unité de compte des finances publiques n'est pas la monnaie, mais la vie humaine du contribuable.",
    anchor: "section-0-1",
  },
  {
    id: "0-2",
    chapter: 0,
    title: "Pourquoi compter en jours de vie",
    content:
      "Traduire un budget en tranches de vies humaines rend immédiatement lisibles les arbitrages politiques.",
    anchor: "section-0-2",
  },
  {
    id: "0-3",
    chapter: 0,
    title: "La formule canonique J = P / (S / 30)",
    content:
      "Jours de vie sacrifiés = Prélèvements divisés par le salaire journalier de référence.",
    anchor: "section-0-3",
  },

  // ── Chapter 1 — Premiers Pas ────────────────────────────────────────
  {
    id: "1-1",
    chapter: 1,
    title: "Créer votre compte VITAE-PUBLICA™",
    content:
      "Renseignez votre email, un mot de passe, votre organisation et le pays d'exercice pour démarrer.",
    anchor: "section-1-1",
  },
  {
    id: "1-2",
    chapter: 1,
    title: "Naviguer dans le tableau de bord",
    content:
      "Le dashboard liste vos projets, vos analyses récentes et vos rapports exportés.",
    anchor: "section-1-2",
  },
  {
    id: "1-3",
    chapter: 1,
    title: "Créer un nouveau projet d'audit",
    content:
      "Saisissez un nom, un pays et une année fiscale, puis accédez au wizard en 7 étapes.",
    anchor: "section-1-3",
  },

  // ── Chapter 2 — Import PDF ──────────────────────────────────────────
  {
    id: "2-1",
    chapter: 2,
    title: "Déposer un PDF budgétaire",
    content:
      "Glissez-déposez un PDF (loi de finances, budget, rapport) dans la zone dédiée, ou cliquez pour parcourir.",
    anchor: "section-2-1",
  },
  {
    id: "2-2",
    chapter: 2,
    title: "Comment uploader un fichier",
    content:
      "Glisser-déposer le PDF dans la zone bleue ou utiliser le bouton Parcourir. Taille maximale : 100 Mo.",
    anchor: "section-2-2",
  },
  {
    id: "2-3",
    chapter: 2,
    title: "Comprendre le score de fiabilité",
    content:
      "Le score de fiabilité mesure la qualité de la lecture du PDF par GPT-4o : vert ≥ 85 %, orange 60-85 %, rouge en dessous.",
    anchor: "section-2-3",
  },
  {
    id: "2-4",
    chapter: 2,
    title: "Corriger les données extraites",
    content:
      "La revue éditable permet de corriger manuellement chaque champ avant validation.",
    anchor: "section-2-4",
  },

  // ── Chapter 3 — Profil contribuable ────────────────────────────────
  {
    id: "3-1",
    chapter: 3,
    title: "Choisir un profil type",
    content:
      "Cinq profils sont proposés : ouvrier SMIG, fonctionnaire moyen, cadre supérieur, retraité et profil personnalisé.",
    anchor: "section-3-1",
  },
  {
    id: "3-2",
    chapter: 3,
    title: "Définir un profil personnalisé",
    content:
      "Saisissez salaire, cotisations, taux d'imposition et consommation annuelle pour un profil sur mesure.",
    anchor: "section-3-2",
  },
  {
    id: "3-3",
    chapter: 3,
    title: "Salaire journalier de référence",
    content:
      "Le salaire journalier est calculé automatiquement : salaire mensuel divisé par 30.",
    anchor: "section-3-3",
  },
  {
    id: "3-4",
    chapter: 3,
    title: "Lecture en jours de vie",
    content:
      "Chaque profil exprime ses prélèvements en jours de vie sacrifiés par an, en mois de vie et en années de carrière.",
    anchor: "section-3-4",
  },

  // ── Chapter 4 — Analyse vitaliste ──────────────────────────────────
  {
    id: "4-1",
    chapter: 4,
    title: "Lancer l'analyse vitaliste",
    content:
      "L'analyse applique les 4 formules vitalistes pour produire les indicateurs individuels et nationaux.",
    anchor: "section-4-1",
  },
  {
    id: "4-2",
    chapter: 4,
    title: "Les 4 formules canoniques",
    content:
      "Jours sacrifiés, ratio vie fiscale, années carrière, années de dette : les quatre indicateurs fondateurs.",
    anchor: "section-4-2",
  },
  {
    id: "4-3",
    chapter: 4,
    title: "Multi-profils comparés",
    content:
      "Comparez jusqu'à trois profils contribuables dans une même analyse.",
    anchor: "section-4-3",
  },

  // ── Chapter 5 — Dashboard & graphiques ─────────────────────────────
  {
    id: "5-1",
    chapter: 5,
    title: "Les 5 KPI cards",
    content:
      "Jours sacrifiés par an, mois de vie, années de carrière, % vie fiscale, années hypothéquées par la dette.",
    anchor: "section-5-1",
  },
  {
    id: "5-2",
    chapter: 5,
    title: "Le donut anatomie des prélèvements",
    content:
      "Répartit les prélèvements entre impôts directs, cotisations, TVA et dette.",
    anchor: "section-5-2",
  },
  {
    id: "5-3",
    chapter: 5,
    title: "Le calendrier 365 jours",
    content:
      "Visualise la libération fiscale : à partir de quelle date du calendrier vous travaillez pour vous.",
    anchor: "section-5-3",
  },
  {
    id: "5-4",
    chapter: 5,
    title: "Treemap budgétaire",
    content:
      "Répartit les postes budgétaires selon leur coût en jours de vie.",
    anchor: "section-5-4",
  },
  {
    id: "5-5",
    chapter: 5,
    title: "Radar sectoriel",
    content:
      "Compare la valeur-vie investie et restituée par secteur : santé, éducation, sécurité, justice, infrastructure.",
    anchor: "section-5-5",
  },

  // ── Chapter 6 — Analyse juridique ──────────────────────────────────
  {
    id: "6-1",
    chapter: 6,
    title: "Bibliothèque juridique",
    content:
      "Téléversez vos codes, lois anticorruption et règlements pour alimenter l'analyse juridique.",
    anchor: "section-6-1",
  },
  {
    id: "6-2",
    chapter: 6,
    title: "Détection des infractions",
    content:
      "Le système identifie les infractions potentielles et les qualifie par niveau de gravité.",
    anchor: "section-6-2",
  },
  {
    id: "6-3",
    chapter: 6,
    title: "Responsabilités engagées",
    content:
      "Chaque infraction est reliée à ses responsabilités pénales, civiles, administratives et professionnelles.",
    anchor: "section-6-3",
  },

  // ── Chapter 7 — Simulation de jugement ─────────────────────────────
  {
    id: "7-1",
    chapter: 7,
    title: "Avertissement pédagogique",
    content:
      "La simulation de jugement est un document strictement pédagogique, sans valeur juridictionnelle.",
    anchor: "section-7-1",
  },
  {
    id: "7-2",
    chapter: 7,
    title: "Lancer la simulation de jugement",
    content:
      "Génère un document de jugement fictif reliant infractions, articles applicables et dispositif vitaliste.",
    anchor: "section-7-2",
  },
  {
    id: "7-3",
    chapter: 7,
    title: "Lecture du dispositif",
    content:
      "Le dispositif articule quatre branches : pénale, civile, administrative, vitaliste.",
    anchor: "section-7-3",
  },

  // ── Chapter 8 — Rapports & export ──────────────────────────────────
  {
    id: "8-1",
    chapter: 8,
    title: "Télécharger le rapport PDF",
    content:
      "Exportez votre audit complet en PDF A4, prêt à diffuser ou archiver.",
    anchor: "section-8-1",
  },
  {
    id: "8-2",
    chapter: 8,
    title: "Exporter en Word (.docx)",
    content:
      "Format éditable pour intégration dans un dossier de presse ou un rapport institutionnel.",
    anchor: "section-8-2",
  },
  {
    id: "8-3",
    chapter: 8,
    title: "Télécharger les données brutes en Excel",
    content:
      "Quatre onglets : budget, calculs vitalistes, analyse juridique, données brutes avec formules.",
    anchor: "section-8-3",
  },
  {
    id: "8-4",
    chapter: 8,
    title: "Poster PNG A3 haute résolution",
    content:
      "Infographie imprimable pour campagnes de sensibilisation et conférences.",
    anchor: "section-8-4",
  },

  // ── Chapter 9 — Fonctionnalités avancées ───────────────────────────
  {
    id: "9-1",
    chapter: 9,
    title: "Simulateur Et si…",
    content:
      "Modulez en temps réel les taux d'impôts pour mesurer l'impact en jours de vie récupérés.",
    anchor: "section-9-1",
  },
  {
    id: "9-2",
    chapter: 9,
    title: "Dette intergénérationnelle",
    content:
      "Projette l'hypothèque sur la vie future des enfants à naître sur 10, 20, 30 ans.",
    anchor: "section-9-2",
  },
  {
    id: "9-3",
    chapter: 9,
    title: "Partage sécurisé d'une analyse",
    content:
      "Générez un lien public avec date d'expiration, révocable à tout moment.",
    anchor: "section-9-3",
  },
  {
    id: "9-4",
    chapter: 9,
    title: "Mode présentation 7 slides",
    content:
      "Basculez en plein écran avec navigation clavier pour conférences et réunions.",
    anchor: "section-9-4",
  },

  // ── Chapter 10 — FAQ & dépannage ───────────────────────────────────
  {
    id: "10-1",
    chapter: 10,
    title: "Mon PDF scanné n'est pas lu",
    content:
      "Un PDF scanné ne contient pas de texte sélectionnable. Passez-le par un OCR (Adobe, Tesseract) avant upload.",
    anchor: "section-10-1",
  },
  {
    id: "10-2",
    chapter: 10,
    title: "L'upload échoue en 502",
    content:
      "Les très gros PDF prennent plusieurs minutes. L'analyse se poursuit en arrière-plan, rafraîchissez la page.",
    anchor: "section-10-2",
  },
  {
    id: "10-3",
    chapter: 10,
    title: "Le score de fiabilité est rouge",
    content:
      "Qualité d'extraction insuffisante : corrigez manuellement les champs dans la revue avant de lancer l'analyse.",
    anchor: "section-10-3",
  },
  {
    id: "10-4",
    chapter: 10,
    title: "Comment réinitialiser ma progression tutoriel ?",
    content:
      "Videz la clé vtpTutorialProgress dans le localStorage de votre navigateur.",
    anchor: "section-10-4",
  },

  // ── Chapter 11 — DEBT-VITA™ : La Dette en Vies ─────────────────────
  {
    id: "11-1",
    chapter: 11,
    title: "DEBT-VITA — Pourquoi mesurer la dette en vies humaines ?",
    content:
      "DEBT-VITA™ étend la théorie vitaliste à l'endettement public : la dette est un prélèvement différé sur le temps de vie des générations présentes et futures.",
    anchor: "section-11-1",
  },
  {
    id: "11-2",
    chapter: 11,
    title: "Les 7 formules vitalistes de la dette (D1 à D7)",
    content:
      "D1 dette par contribuable, D2 jours de vie hypothéqués, D3 années de vie, D4 part de carrière sacrifiée, D5 service annuel en vies, D6 charge intergénérationnelle, D7 TAVD taux d'aliénation.",
    anchor: "section-11-2",
  },
  {
    id: "11-3",
    chapter: 11,
    title: "Dashboard Dette & Vies — bannière d'alerte et 8 graphiques",
    content:
      "Bannière 4 niveaux (soutenable, sous tension, critique, insoutenable), thermomètre vitaliste, donut créanciers, calendrier de libération, projection 3 scénarios, charge intergénérationnelle, dette vs fiscalité, intérêts vs capital, treemap.",
    anchor: "section-11-3",
  },
  {
    id: "11-4",
    chapter: 11,
    title: "Comparateur dette multi-pays / multi-années",
    content:
      "Comparez deux projets sauvegardés ou un projet face à une référence FMI/OCDE saisie manuellement (pays, ratio dette/PIB, service/recettes).",
    anchor: "section-11-4",
  },
  {
    id: "11-5",
    chapter: 11,
    title: "Analyse narrative DEBT-VITA — un appel GPT-4o cached",
    content:
      "Lecture existentielle et juridique de la situation d'endettement par GPT-4o : titre, synthèse, analyse intergénérationnelle, points critiques, recommandations vitalistes, citation, conclusion. Un seul appel LLM, mise en cache automatique.",
    anchor: "section-11-5",
  },
  {
    id: "11-6",
    chapter: 11,
    title: "Rapport DEBT-VITA™ PDF, Word, Excel — 12 pages thème sombre",
    content:
      "Couverture noire avec badge d'alerte coloré, méthodologie, 8 graphiques en haute résolution, projection décennale, narrative, qualification juridique Maastricht/FMI/Reinhart-Rogoff/TAVD. Excel avec 5 onglets dont des formules vivantes.",
    anchor: "section-11-6",
  },
];

export const FUSE_OPTIONS = {
  keys: [
    { name: "title", weight: 0.55 },
    { name: "content", weight: 0.45 },
  ],
  threshold: 0.38,
  ignoreLocation: true,
  includeMatches: true,
  minMatchCharLength: 2,
};
